import { em } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import React, { createContext, ReactNode, useContext } from "react";

export interface BreakpointContextValue {
  isXs: boolean;
  isSm: boolean;
  isMd: boolean;
  isLg: boolean;
  isXl: boolean;
  currentBreakpoint: "xs" | "sm" | "md" | "lg" | "xl";
}

const BreakpointContext = createContext<BreakpointContextValue | undefined>(
  undefined
);

interface BreakpointProviderProps {
  children: ReactNode;
}

export function BreakpointProvider({ children }: BreakpointProviderProps) {
  // Mantine breakpoints in em units
  const isXl = useMediaQuery(`(min-width: ${em(1408)})`);
  const isLg = useMediaQuery(`(min-width: ${em(1200)})`);
  const isMd = useMediaQuery(`(min-width: ${em(992)})`);
  const isSm = useMediaQuery(`(min-width: ${em(768)})`);
  const isXs = useMediaQuery(`(min-width: ${em(576)})`);

  // Determine current breakpoint (highest matching breakpoint)
  const currentBreakpoint: BreakpointContextValue["currentBreakpoint"] = isXl
    ? "xl"
    : isLg
      ? "lg"
      : isMd
        ? "md"
        : isSm
          ? "sm"
          : "xs";

  const value: BreakpointContextValue = {
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,
    currentBreakpoint,
  };

  return (
    <BreakpointContext.Provider value={value}>
      {children}
    </BreakpointContext.Provider>
  );
}

export function useBreakpoint(): BreakpointContextValue {
  const context = useContext(BreakpointContext);
  if (context === undefined) {
    throw new Error("useBreakpoint must be used within a BreakpointProvider");
  }
  return context;
}

// Utility function to resolve responsive values using the context
export function useResponsiveValue<T>(
  value:
    | T
    | {
        base?: T;
        xs?: T;
        sm?: T;
        md?: T;
        lg?: T;
        xl?: T;
      }
): T | undefined {
  const { isXs, isSm, isMd, isLg, isXl } = useBreakpoint();

  // Check if value is a responsive object
  if (
    value &&
    typeof value === "object" &&
    !React.isValidElement(value) &&
    !Array.isArray(value) &&
    typeof value !== "string" &&
    typeof value !== "number"
  ) {
    // Check if it has responsive properties
    const responsiveObj = value as {
      base?: T;
      xs?: T;
      sm?: T;
      md?: T;
      lg?: T;
      xl?: T;
    };

    const hasResponsiveProps = ["base", "xs", "sm", "md", "lg", "xl"].some(
      (key) => key in responsiveObj
    );

    if (hasResponsiveProps) {
      // Find the appropriate value based on current breakpoint
      if (isXl && responsiveObj.xl !== undefined) return responsiveObj.xl;
      if (isLg && responsiveObj.lg !== undefined) return responsiveObj.lg;
      if (isMd && responsiveObj.md !== undefined) return responsiveObj.md;
      if (isSm && responsiveObj.sm !== undefined) return responsiveObj.sm;
      if (isXs && responsiveObj.xs !== undefined) return responsiveObj.xs;

      // Fallback to base
      return responsiveObj.base;
    }
  }

  // Return the value as-is if it's not a responsive object
  return value as T;
}
